/** Compatibility names for callers written while IndexedDB was called the
 * archive. There is one backend and one packed mirror, owned by docStore.ts;
 * no separate archive tier, eviction or background mirroring remains. */
import {
  docEnvelope,
  unwrapDocumentEnvelope,
  documentBackend,
  setDocBackend,
  writeDocText,
  deleteDocText,
  clearDocs,
  docIds,
  shelfOpen,
} from './docStore'
import { unpackText } from './pack'
import { packTextOffThread } from './packHost'
import {
  activeDocumentId,
  readStoredSessionJson,
  storedSessionJson,
  writeLast,
  type StoredSession,
} from './persist'
import { forgottenElsewhere, markMoved, markNow } from './forgotten'
export { openDb, setArchiveOpener } from './docStore'
export const archiveEnvelope = docEnvelope
export const unwrapArchive = unwrapDocumentEnvelope
export interface ArchiveKV {
  get(id: string): Promise<string | null>
  ids?(): Promise<string[]>
  active?(): Promise<string | null>
  activate?(id: string, version: number): Promise<boolean>
  forget?(id: string): Promise<void>
  putIfNewer(
    id: string,
    text: string,
    version: number,
    sweep: string | null,
    expectedVersion?: number | null,
  ): Promise<boolean>
  del(id: string): Promise<void>
  sweepExcept(sweep: string): Promise<void>
}
/** The test adapter switches the sole shelf, never installs a second store. */
export function setArchiveBackend(kv: ArchiveKV | null): void {
  setDocBackend(
    kv
      ? {
          ...kv,
          all: async () => {
            const rows: Array<{ id: string; raw: string }> = []
            for (const id of kv.ids ? await kv.ids() : docIds()) {
              const raw = await kv.get(id)
              if (raw !== null) rows.push({ id, raw })
            }
            return rows
          },
          putIfNewer: (id, text, version, expected, sweep) =>
            kv.putIfNewer(id, text, version, sweep ?? null, expected),
          clear: () => kv.sweepExcept(`${Date.now()}:${Math.random()}`),
        }
      : null,
  )
}
export type DurableWriteFailure = 'unavailable' | 'conflict' | 'forgotten' | 'unreadable'
export class DurableWriteError extends Error {
  constructor(
    readonly reason: DurableWriteFailure,
    /** The bytes DID commit, and the forget record moved while they were being
     *  written. Said, because one tab's own deletion in flight makes that a
     *  question the deletion's answer settles, not a failure (D331). */
    readonly committed = false,
  ) {
    super(reason)
  }
}
function shelf() {
  const kv = documentBackend()
  if (!kv || !shelfOpen()) throw new DurableWriteError('unavailable')
  return kv
}
/** A document's stored form: its JSON, and `packText` of exactly that JSON —
 * pending while it is packed off the main thread (`packHost.ts`). */
export interface BuiltDocument {
  text: string
  packed: Promise<string>
}
/** `built` is the autosave's one build of this same `doc`, so a save serialises
 * and deflates once. It spares only that work: the read-back below still runs over
 * the text, because the build is trusted to be of `doc`, never to be readable.
 *
 * The packing runs off the main thread (`packHost.ts`), and the codec reads the
 * text back once it has landed: still before the transaction, as it always was,
 * but in a task of its own rather than the one that serialised the document — on
 * the fixture after an edit, 150 ms of `JSON.stringify` and 45 ms of read-back
 * that together were one block. The generation taken first spans the wait, so a
 * forget landing during it still refuses below. */
export async function writeDurable(
  doc: StoredSession,
  expectedVersion?: number | null,
  built?: BuiltDocument,
): Promise<string> {
  const id = doc.session.id
  const generation = markNow(id)
  if (forgottenElsewhere(id)) throw new DurableWriteError('forgotten')
  const text = built?.text ?? storedSessionJson(doc)
  const packed = await (built?.packed ?? packTextOffThread(text))
  if (!readStoredSessionJson(text)) throw new DurableWriteError('unreadable')
  if (!(await writeDocText(id, packed, doc.savedAt, expectedVersion)))
    throw new DurableWriteError(markMoved(id, generation) ? 'forgotten' : 'conflict')
  if (markMoved(id, generation)) throw new DurableWriteError('forgotten', true)
  return text
}
export async function durableDocumentIds(): Promise<string[]> {
  const kv = shelf()
  return kv.ids ? kv.ids() : (await kv.all()).map((row) => row.id)
}
export async function readDurableActiveId(): Promise<string | null> {
  const kv = shelf()
  return kv.active ? kv.active() : activeDocumentId()
}
export async function activateDurableDocument(id: string, version: number): Promise<void> {
  const kv = shelf()
  if (kv.activate) {
    if (!(await kv.activate(id, version))) throw new DurableWriteError('conflict')
  } else if (!writeLast(id)) throw new DurableWriteError('unavailable')
}
export async function readDurableRecord(
  id: string,
): Promise<{ text: string; version: number } | null> {
  const generation = markNow(id)
  if (forgottenElsewhere(id)) return null
  const raw = await shelf().get(id)
  if (markMoved(id, generation)) throw new DurableWriteError('forgotten')
  if (raw === null) return null
  const held = unwrapDocumentEnvelope(raw)
  const text = unpackText(held.text)
  if (text === null) throw new DurableWriteError('unreadable')
  return { text, version: held.v }
}
export async function readArchived(id: string): Promise<string | null> {
  return (await readDurableRecord(id))?.text ?? null
}
export function parseArchived(text: string): StoredSession | null {
  const plain = unpackText(text)
  return plain === null ? null : readStoredSessionJson(plain)
}
/** Acknowledged, as `deleteDocText` is: resolves once the shelf has committed. */
export function forgetArchived(id: string): Promise<void> {
  return deleteDocText(id)
}
export function forgetAllArchived(): Promise<void> {
  return clearDocs()
}
